import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { TextResponse } from '@core-api/presentation/types/response';
import { useCallback, useEffect, useRef } from 'react';
import { trackUser } from '@api/tracking/client';
import type { VisualizationState } from '../../../../utils/getVisualizationState';
import { useOpenSlideState } from '../utils/useOpenSlideState';
import { FocusedView } from '../FocusedView';
import { useOpenEndedPresentationContext } from '../../OpenEndedPresentationContext';
import { ResponsesView } from './ResponsesView';

interface ResponsesViewDataHandlerProps {
  responses: TextResponse[];
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  shouldHideResults: boolean;
  numberOfColumns: number;
  visualizationState: VisualizationState;
}

const useFocusedView = ({
  visualizationState,
  hasResponses,
}: {
  visualizationState: VisualizationState;
  hasResponses: boolean;
}) => {
  const [{ activeResponseIndex }, setSlideState] = useOpenSlideState();

  const shouldShowFocusedView =
    activeResponseIndex !== null &&
    activeResponseIndex !== undefined &&
    hasResponses;

  const prevVisualizationStateRef = useRef<VisualizationState | null>(null);

  const handleSetActiveResponseIndex = useCallback(
    (index: number | null) => {
      setSlideState({ activeResponseIndex: index });
    },
    [setSlideState],
  );

  const handleOpenFocusedView = (index: number) => {
    handleSetActiveResponseIndex(index);
    trackUser({
      event: 'Show one by one answer',
      properties: {
        placement: 'Presentation view menu',
        context: 'Presentation view',
      },
    });
  };

  useEffect(() => {
    // show the results view (not one by one) if arriving at 'view-results'
    // state and an activeResponseIndex is selected since before
    if (
      visualizationState === 'view-results' &&
      prevVisualizationStateRef.current !== visualizationState &&
      shouldShowFocusedView
    ) {
      handleSetActiveResponseIndex(null);
    }
    prevVisualizationStateRef.current = visualizationState;
  }, [handleSetActiveResponseIndex, shouldShowFocusedView, visualizationState]);

  return {
    handleOpenFocusedView,
    activeResponseIndex,
    handleSetActiveResponseIndex,
    shouldShowFocusedView,
  };
};

export const ResponsesOrFocusedView = ({
  visualizationState,
  CanvasVisualizationArea,
  shouldHideResults,
  responses,
  numberOfColumns,
}: ResponsesViewDataHandlerProps) => {
  const { isReadonly } = useOpenEndedPresentationContext();

  const shouldShowUpvotes = visualizationState === 'view-results';
  const sortedResponses = [...responses].sort(
    (a, b) => b.voteCount - a.voteCount,
  );
  const displayResponses = shouldShowUpvotes ? sortedResponses : responses;

  const {
    handleOpenFocusedView,
    activeResponseIndex,
    handleSetActiveResponseIndex,
    shouldShowFocusedView,
  } = useFocusedView({
    visualizationState,
    hasResponses: displayResponses.length > 0,
  });

  if (shouldShowFocusedView) {
    return (
      <FocusedView
        onDismiss={() => handleSetActiveResponseIndex(null)}
        currentIndex={activeResponseIndex ?? 0}
        responses={displayResponses}
        setCurrentIndex={handleSetActiveResponseIndex}
        showUpvotes={shouldShowUpvotes}
        readonly={isReadonly}
      />
    );
  }

  return (
    <ResponsesView
      CanvasVisualizationArea={CanvasVisualizationArea}
      shouldHideResults={shouldHideResults}
      shouldShowUpvotes={shouldShowUpvotes}
      onOpenOneByOneView={handleOpenFocusedView}
      responses={displayResponses}
      numberOfColumns={numberOfColumns}
    />
  );
};
